import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TimerProgressBarComponent } from '@frontend/sports/common/ui/progress-bar';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { SlideComponent, SlideshowComponent } from '@frontend/sports/slideshow/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { sumBy } from 'lodash-es';
import { distinctUntilChanged } from 'rxjs';

import { StoryContentTrackingService } from '../story-content-tracking.service';
import { ContentStory, ContentSubStory, NavigationPayload, StoryNavigation, SubStoryType } from '../story-content.model';
import { StoryContentService } from '../story-content.service';
import { RotationMessageComponent } from './rotation-message.component';
import { StoryBodyComponent } from './story-body.component';

@Component({
    selector: 'ms-story',
    templateUrl: './story.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [trigger('storySlideAnimation', [transition(':leave', animate(350, style({ opacity: '1' })))])],
    providers: [StoryContentTrackingService],
    imports: [RotationMessageComponent, TimerProgressBarComponent, StoryBodyComponent, SlideshowComponent, SlideComponent],
})
export class StoryComponent implements OnInit {
    private activeModal = inject(ActiveModal);
    private storyService = inject(StoryContentService);
    private storyTracking = inject(StoryContentTrackingService);
    private deviceService = inject(DeviceService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    @Input() stories: ContentStory[];
    @Input() activeStory: number;

    @HostBinding('class') className = 'story-content';
    @HostBinding('class.rotation-msg') showRotationMessage: boolean;

    get story(): ContentStory {
        return this.stories[this.activeStory];
    }

    get subStory(): ContentSubStory {
        return this.story.subStories[this.activeSubStory];
    }

    activeSubStory = 0;

    progressBarPaused = false;
    progressBarWidth: { [key: string]: string } = {};

    disableDesktopSwipe: boolean;

    private trackStory = false;
    private lastViewedSubStories: number[] = [];
    private readonly destroyRef = inject(DestroyRef);

    @ViewChild(SlideshowComponent) private slideshowComponent: SlideshowComponent;

    constructor() {
        this.storyService.statusChange.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe((value) => {
            this.progressBarPaused = value;
            this.changeDetectorRef.markForCheck();
        });
        this.storyService.navigationChange.pipe(takeUntilDestroyed()).subscribe((payload) => {
            this.nextAndPreviousActions(payload);
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnInit(): void {
        this.disableDesktopSwipe = !this.deviceService.isMobilePhone;
        this.deviceService.orientation.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.orientationChanged());
        this.activeSubStory = this.getCurrentSubStoryIndex(this.activeStory);

        this.setProgressBarWidth();
        this.trackStoryAndSubstoryOnLoad(false, false, true);

        if (!(this.showRotationMessage && this.subStory.type === SubStoryType.Video)) {
            this.setProgressBarStatus(false);
        }
    }

    closePopup(updateData: boolean = true, closeButtonClicked: boolean = false): void {
        this.activeModal.close();
        if (updateData) {
            this.updateViewedData();
        }
        if (closeButtonClicked) {
            this.storyTracking.trackPopupClose(false, this.story.title);
        }
    }

    private nextAndPreviousActions(payload: NavigationPayload): void {
        switch (payload.action) {
            case StoryNavigation.NextStory:
                this.nextStory();
                break;
            case StoryNavigation.PreviousStory:
                this.previousStory();
                break;
            case StoryNavigation.NextSubStory:
                this.nextSubStory(false, payload.payload.storyId, payload.payload.subStoryId);
                break;
            case StoryNavigation.NextSubStoryWithoutTracking:
                this.nextSubStory(false, payload.payload.storyId, payload.payload.subStoryId);
                break;
            case StoryNavigation.PreviousSubStory:
                this.previousSubStory();
                break;
        }
    }

    storySlideChanged(storyIndex: number): void {
        if (this.progressBarPaused) {
            this.setProgressBarStatus(false);
        }
        if (this.trackStory) {
            this.trackStoryAndSubstory(true, storyIndex > this.activeStory);
        }

        this.trackStoryAndSubstoryOnLoad(true, storyIndex > this.activeStory);
        this.updateViewedData();
        this.lastViewedSubStories[this.activeStory] = this.activeSubStory;
        this.activeStory = storyIndex;
        this.activeSubStory = this.getCurrentSubStoryIndex(this.activeStory);
        this.setProgressBarWidth();
        this.setStoryTrackingStatus(true);
    }

    private nextStory(isManualClick: boolean = true): void {
        this.setStoryTrackingStatus(isManualClick);
        this.slideshowComponent.swipeLeft(false);
    }

    private previousStory(): void {
        this.setStoryTrackingStatus(true);
        this.slideshowComponent.swipeRight(false);
    }

    nextSubStory(isManualClick: boolean = true, storyId: string, subStoryId: string): void {
        if (this.story.id !== storyId || this.subStory.id !== subStoryId) {
            return;
        }
        this.setStoryTrackingStatus(isManualClick);
        const lastSubStoryIndex = this.story.subStories.length - 1;

        if (this.progressBarPaused) {
            this.setProgressBarStatus(false);
        }

        if (this.activeSubStory === lastSubStoryIndex) {
            this.nextStory(isManualClick);
        } else {
            if (isManualClick) {
                this.trackStoryAndSubstory(false, true);
            }
            this.trackStoryAndSubstoryOnLoad(false, true);
            this.updateViewedData();
            this.activeSubStory++;
        }
    }

    private previousSubStory(): void {
        this.setStoryTrackingStatus(true);

        if (this.progressBarPaused) {
            this.setProgressBarStatus(false);
        }

        if (!this.activeSubStory) {
            this.previousStory();
        } else {
            this.updateViewedData();
            this.trackStoryAndSubstory(false, false);
            this.trackStoryAndSubstoryOnLoad(false, false);
            this.activeSubStory--;
        }
    }

    setProgressBarStatus(pauseProgressBar: boolean): void {
        if (pauseProgressBar) {
            this.storyService.pause();
        } else {
            this.storyService.play();
        }
    }

    private getCurrentSubStoryIndex(active: number): number {
        const unViewedSubStoryIndex = this.story.subStories.findIndex((subStory) => !this.storyService.isViewed(subStory));
        const lastViewedSubStoryIndex = this.lastViewedSubStories[active];

        if (unViewedSubStoryIndex >= 0) {
            return unViewedSubStoryIndex;
        } else if (lastViewedSubStoryIndex) {
            return lastViewedSubStoryIndex;
        }

        return 0;
    }

    private updateViewedData(): void {
        const story = this.story;
        const subStory = story.subStories[this.activeSubStory];

        this.storyService.setViewed(subStory);
    }

    private setProgressBarWidth(): void {
        const subStories = this.story.subStories;
        const sumOfDurations = sumBy(subStories, (subStory) => subStory.duration);

        if (!sumOfDurations) {
            return;
        }

        subStories.forEach((subStory) => {
            this.progressBarWidth[subStory.id] = `${((subStory.duration * 100) / sumOfDurations).toFixed(2)}%`;
        });
    }

    private orientationChanged(): void {
        this.showRotationMessage = this.deviceService.isMobilePhone && this.deviceService.currentOrientation === 'landscape';
        this.setProgressBarStatus(this.showRotationMessage);
        this.changeDetectorRef.markForCheck();
    }

    private setStoryTrackingStatus(trackStory: boolean): void {
        this.trackStory = trackStory;
    }

    private trackStoryAndSubstory(isStory: boolean, isNext: boolean): void {
        const storyTitle = this.getStoryTitleForTracking(isStory, isNext);
        this.storyTracking.trackStoryAndSubstory(isStory, isNext, storyTitle);
    }

    private trackStoryAndSubstoryOnLoad(isStory: boolean, isNext: boolean, isPopupLoad: boolean = false): void {
        const storyTitle = this.getStoryTitleForTracking(isStory, isNext);
        this.storyTracking.trackStoryAndSubstoryOnLoad(isStory, isNext, storyTitle, isPopupLoad);
    }

    private getStoryTitleForTracking(isStory: boolean, isNext: boolean): string {
        if (isStory) {
            return isNext ? this.stories[this.activeStory + 1].title : this.stories[this.activeStory - 1].title;
        }

        return this.story.title;
    }
}
